import { FOCO_VISIVEL } from 'utils/foco';

export type TomDoIndicador = 'rosa' | 'verde' | 'amarelo' | 'ciano' | 'azul';

const COR_DO_PONTO: Record<TomDoIndicador, string> = {
  rosa: 'bg-brand-pink',
  verde: 'bg-brand-green',
  amarelo: 'bg-brand-yellow',
  ciano: 'bg-brand-cyan-light',
  azul: 'bg-brand-blue',
};

export interface IndicadorDaFila {
  chave: string;
  rotulo: string;
  /** Valor já calculado pela página (ex.: `"3/10"` a partir de `quotas`). */
  valor: string;
  tom?: TomDoIndicador;
  /** Presente só quando o contador corresponde a uma opção dos filtros atuais: o cartão vira um botão de filtro. */
  onFiltrar?: () => void;
  /** O filtro deste cartão está aplicado. */
  ativo?: boolean;
}

export interface IndicadoresDaFilaProps {
  /** Contadores que a página mostra hoje, nas mesmas condições; o componente não recalcula regra (RDN-48). */
  indicadores: IndicadorDaFila[];
}

const CARTAO = 'relative flex min-w-0 flex-col gap-[11px] rounded-jj-lg border-[1.5px] px-[15px] pb-[13px] pt-3.5 text-left';

/** Círculo de marcação do design no canto do cartão que filtra; preenchido quando o filtro está aplicado. */
function MarcaDoFiltro({ ativo }: { ativo: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`absolute right-[13px] top-[13px] flex h-[18px] w-[18px] items-center justify-center rounded-full border-[1.5px] ${
        ativo ? 'border-urgent bg-urgent text-brand-white' : 'border-surface-300 bg-brand-white text-transparent'
      }`}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6 9 17l-5-5" />
      </svg>
    </span>
  );
}

function Conteudo({ indicador }: { indicador: IndicadorDaFila }) {
  return (
    <>
      <span className="flex min-h-[32px] items-start gap-2 pr-6">
        {indicador.tom && (
          <span aria-hidden="true" className={`mt-1 h-2 w-2 shrink-0 rounded-full ${COR_DO_PONTO[indicador.tom]}`} />
        )}
        <span className="text-[11.5px] leading-[1.35] text-content">{indicador.rotulo}</span>
      </span>
      {indicador.onFiltrar && <MarcaDoFiltro ativo={!!indicador.ativo} />}
      <span className="flex items-baseline gap-2">
        <b className="font-display text-[26px] leading-none text-brand-blue">{indicador.valor}</b>
        {indicador.ativo && (
          // SPEC_DEVIATION: "Filtrando" em `text-urgent` no lugar do `brand-pink` do design (2,9:1 no branco).
          <span className="text-[9.5px] font-bold uppercase tracking-[0.05em] text-urgent">Filtrando</span>
        )}
      </span>
    </>
  );
}

/** Cartões de contador da fila do gestor (RDN-48); os que filtram expõem o estado com `aria-pressed` (RDN-62). */
export default function IndicadoresDaFila({ indicadores }: IndicadoresDaFilaProps) {
  if (!indicadores.length) return null;

  return (
    <div>
      <h3 id="indicadores-da-fila" className="m-0 mb-[9px] text-[10px] font-bold uppercase tracking-[0.06em] text-content-soft">
        Filtrar a lista
      </h3>
      <ul aria-labelledby="indicadores-da-fila" className="m-0 grid list-none grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-2.5 p-0">
        {indicadores.map(indicador => (
          <li key={indicador.chave} className="flex">
            {indicador.onFiltrar ? (
              <button
                type="button"
                aria-pressed={!!indicador.ativo}
                onClick={indicador.onFiltrar}
                className={`${CARTAO} w-full cursor-pointer ${
                  indicador.ativo ? 'border-urgent bg-brand-white' : 'border-surface-300 bg-brand-white hover:border-brand-blue'
                } ${FOCO_VISIVEL}`}
              >
                <Conteudo indicador={indicador} />
              </button>
            ) : (
              <div className={`${CARTAO} w-full border-surface-300 bg-brand-white`}>
                <Conteudo indicador={indicador} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
